import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ApiMethod, AuthEndPoints } from '../../helper/apiconstant';
import { Httpservice } from '../../services/httpservice';
import { SscShellComponent } from '../ssc/ssc-shell/ssc-shell';
import {
  DashboardAnalytics,
  DashboardBreakdown,
  DashboardQuality,
  DashboardSummary,
  DashboardTrendPoint,
} from './dashboard.interfaces';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, SscShellComponent],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements OnInit {
  protected analytics: DashboardAnalytics = this.emptyAnalytics();
  protected loading = false;
  protected errorMessage = '';

  constructor(
    private readonly httpSvc: Httpservice,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadAnalytics();
  }

  protected loadAnalytics(): void {
    this.loading = true;
    this.errorMessage = '';
    this.httpSvc.requestCall(AuthEndPoints.DASHBOARD_TRANSLATION_ANALYTICS, ApiMethod.GET, '', false, { suppressToast: true }).subscribe({
      next: (response) => {
        this.analytics = this.normalizeAnalytics(response?.value);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Unable to load dashboard analytics.';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  protected get summary(): DashboardSummary {
    return this.analytics.summary;
  }

  protected get quality(): DashboardQuality {
    return this.analytics.quality;
  }

  protected get hasQuestions(): boolean {
    return this.summary.total_questions > 0;
  }

  protected maxTrendValue(): number {
    return Math.max(...this.analytics.daily_trend.map((item) => Math.max(item.questions, item.translations)), 1);
  }

  protected barHeight(value: number): number {
    return Math.max((Number(value || 0) / this.maxTrendValue()) * 100, value > 0 ? 8 : 0);
  }

  protected breakdownPercent(item: DashboardBreakdown, rows: DashboardBreakdown[]): number {
    const total = rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
    if (!total) return 0;
    return Math.round((Number(item.count || 0) / total) * 100);
  }

  protected confidenceClass(value: number): string {
    if (value >= 90) return 'good';
    if (value >= 70) return 'warn';
    return 'risk';
  }

  protected formatPercent(value: number): string {
    return `${Math.round(Number(value || 0))}%`;
  }

  protected metricWidth(value: number, max = 100): number {
    return Math.min(Math.max((Number(value || 0) / max) * 100, 0), 100);
  }

  private normalizeAnalytics(value: unknown): DashboardAnalytics {
    const record = this.asRecord(value);
    const analytics = this.emptyAnalytics();
    analytics.summary = this.asRecord(record['summary']) as unknown as DashboardSummary;
    analytics.quality = this.asRecord(record['quality']) as unknown as DashboardQuality;
    analytics.daily_trend = this.arrayOfRecords(record['daily_trend']) as unknown as DashboardTrendPoint[];
    analytics.status_breakdown = this.arrayOfRecords(record['status_breakdown']) as unknown as DashboardBreakdown[];
    analytics.upload_type_breakdown = this.arrayOfRecords(record['upload_type_breakdown']) as unknown as DashboardBreakdown[];
    analytics.language_pairs = this.arrayOfRecords(record['language_pairs']) as unknown as DashboardAnalytics['language_pairs'];
    analytics.subjects = this.arrayOfRecords(record['subjects']) as unknown as DashboardAnalytics['subjects'];
    analytics.recent_questions = this.arrayOfRecords(record['recent_questions']) as unknown as DashboardAnalytics['recent_questions'];
    analytics.asset_summary = this.asRecord(record['asset_summary']) as unknown as DashboardAnalytics['asset_summary'];
    return this.withDefaults(analytics);
  }

  private withDefaults(analytics: DashboardAnalytics): DashboardAnalytics {
    const empty = this.emptyAnalytics();
    return {
      ...empty,
      ...analytics,
      summary: { ...empty.summary, ...analytics.summary },
      quality: { ...empty.quality, ...analytics.quality },
      asset_summary: { ...empty.asset_summary, ...analytics.asset_summary },
    };
  }

  private emptyAnalytics(): DashboardAnalytics {
    return {
      summary: {
        total_questions: 0,
        total_translations: 0,
        total_files: 0,
        total_images: 0,
        target_languages: 0,
        today_questions: 0,
      },
      quality: {
        avg_confidence: 0,
        avg_adequacy: 0,
        avg_fluency: 0,
        high_confidence: 0,
        low_confidence: 0,
      },
      daily_trend: [],
      status_breakdown: [],
      upload_type_breakdown: [],
      language_pairs: [],
      subjects: [],
      recent_questions: [],
      asset_summary: {
        glossary_terms: 0,
        tm_segments: 0,
        corpus_entries: 0,
        benchmark_items: 0,
      },
    };
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return typeof value === 'object' && value !== null ? value as Record<string, unknown> : {};
  }

  private arrayOfRecords(value: unknown): Record<string, unknown>[] {
    return Array.isArray(value) ? value.filter((item) => typeof item === 'object' && item !== null) as Record<string, unknown>[] : [];
  }
}
